import { useState, useEffect } from 'react';
import { supabase } from './supabase';
import { supabaseData } from './supabaseData';
import { Clock, User, Edit2, Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { useTheme } from './ThemeContext';

/**
 * AuditLogViewer - Displays activity history for any database record
 * 
 * Usage:
 * <AuditLogViewer tableName="leads" recordId={lead.id} />
 * <AuditLogViewer tableName="quotes" recordId={quote.id} />
 * <AuditLogViewer tableName="invoices" recordId={invoice.id} />
 * 
 * Features:
 * - Collapsed by default (unobtrusive)
 * - Shows WHO made changes
 * - Shows WHEN changes happened
 * - Shows WHAT changed (old → new values)
 * - Scrollable history
 * - Mobile responsive
 */
export default function AuditLogViewer({ tableName, recordId }) {
  const { colors } = useTheme();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (recordId) {
      fetchAuditLog();
    }
  }, [recordId, tableName]);

  const fetchAuditLog = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabaseData
        .from('audit_log')
        .select('*')
        .eq('table_name', tableName)
        .eq('record_id', recordId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setLogs(data || []);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'created': return <Plus size={14} />;
      case 'updated': return <Edit2 size={14} />;
      case 'deleted': return <Trash2 size={14} />;
      default: return <Clock size={14} />;
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'created': return '#4ecca3';
      case 'updated': return '#3498db';
      case 'deleted': return '#e74c3c';
      default: return colors.textMuted;
    }
  };

  const formatFieldName = (key) => {
    // Convert snake_case to Title Case
    return key
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const formatValue = (value) => {
    if (value === null || value === undefined) return 'null';
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    if (typeof value === 'object') return JSON.stringify(value);
    if (typeof value === 'string' && value.length > 50) {
      return value.substring(0, 50) + '...';
    }
    return String(value);
  };

  const formatChanges = (changes, action) => {
    if (!changes) return null;
    
    // For 'created' action, show the new record (but hide system fields)
    if (action === 'created' && changes.new) {
      return (
        <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: colors.textMuted }}>
          <div style={{ color: colors.textSecondary, marginBottom: '0.25rem' }}>Initial values:</div>
          {Object.entries(changes.new)
            .filter(([key]) => !['id', 'created_at', 'updated_at'].includes(key))
            .slice(0, 5) // Show first 5 fields only
            .map(([key, value]) => (
              <div key={key} style={{ marginLeft: '1rem', marginBottom: '0.15rem' }}>
                <span style={{ color: colors.textSecondary }}>{formatFieldName(key)}:</span>{' '}
                <span style={{ color: colors.text }}>{formatValue(value)}</span>
              </div>
            ))}
        </div>
      );
    }
    
    // For 'updated' action, show what changed
    if (action === 'updated') {
      const changedFields = Object.entries(changes).filter(
        ([key]) => !['id', 'created_at', 'updated_at'].includes(key)
      );
      
      if (changedFields.length === 0) return null;

      return (
        <div style={{ marginTop: '0.5rem', fontSize: '0.8rem' }}>
          {changedFields.map(([key, value]) => {
            if (value?.old !== undefined && value?.new !== undefined) {
              return (
                <div key={key} style={{ marginBottom: '0.35rem' }}>
                  <div style={{ color: colors.textSecondary, marginBottom: '0.15rem' }}>
                    {formatFieldName(key)}:
                  </div>
                  <div style={{ marginLeft: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <span style={{ 
                      color: '#e74c3c', 
                      textDecoration: 'line-through',
                      padding: '0.125rem 0.375rem',
                      background: '#2a1a1a',
                      borderRadius: '4px'
                    }}>
                      {formatValue(value.old)}
                    </span>
                    <span style={{ color: colors.textMuted }}>→</span>
                    <span style={{ 
                      color: '#4ecca3',
                      padding: '0.125rem 0.375rem',
                      background: '#1a2a1a',
                      borderRadius: '4px'
                    }}>
                      {formatValue(value.new)}
                    </span>
                  </div>
                </div>
              );
            }
            return null;
          })}
        </div>
      );
    }

    // For 'deleted' action
    if (action === 'deleted' && changes.old) {
      return (
        <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#e74c3c' }}>
          Record was permanently deleted
        </div>
      );
    }
    
    return null;
  };

  // Don't render if no record ID
  if (!recordId) {
    return null;
  }

  // Loading state
  if (loading) {
    return (
      <div style={{ 
        borderTop: `1px solid ${colors.border}`,
        marginTop: '2rem',
        paddingTop: '1rem',
        textAlign: 'center',
        color: colors.textMuted,
        fontSize: '0.9rem'
      }}>
        <Clock size={16} style={{ animation: 'spin 1s linear infinite' }} />
        <span style={{ marginLeft: '0.5rem' }}>Loading history...</span>
      </div>
    );
  }

  // No history yet
  if (logs.length === 0) {
    return null; // Don't show anything if no audit trail
  }

  return (
    <div style={{
      borderTop: `1px solid ${colors.border}`,
      marginTop: '2rem',
      paddingTop: '1rem'
    }}>
      {/* Collapsible Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          width: '100%',
          padding: '0.75rem 1rem',
          background: colors.bgCard,
          border: `1px solid ${colors.border}`,
          borderRadius: '6px',
          color: colors.textMuted,
          fontSize: '0.9rem',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          transition: 'all 0.2s'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = colors.accent;
          e.currentTarget.style.color = colors.text;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = colors.border;
          e.currentTarget.style.color = colors.textMuted;
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Clock size={16} />
          <span>Activity History ({logs.length} {logs.length === 1 ? 'event' : 'events'})</span>
        </div>
        {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>

      {/* Expanded Log Entries */}
      {expanded && (
        <div style={{
          marginTop: '0.5rem',
          maxHeight: '400px',
          overflowY: 'auto',
          background: colors.bgCard,
          border: `1px solid ${colors.border}`,
          borderRadius: '6px',
          padding: '1rem'
        }}>
          {logs.map((log, index) => (
            <div
              key={log.id}
              style={{
                padding: '0.75rem',
                background: colors.bgCard,
                borderRadius: '6px',
                borderLeft: `3px solid ${getActionColor(log.action)}`,
                marginBottom: index < logs.length - 1 ? '0.75rem' : 0
              }}
            >
              {/* Header: Action + User + Time */}
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.75rem',
                marginBottom: '0.5rem'
              }}>
                <div style={{ 
                  color: getActionColor(log.action),
                  marginTop: '0.125rem'
                }}>
                  {getActionIcon(log.action)}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ 
                    fontSize: '0.9rem', 
                    color: colors.text,
                    fontWeight: '600',
                    textTransform: 'capitalize'
                  }}>
                    {log.action}
                  </div>
                  <div style={{ 
                    fontSize: '0.8rem', 
                    color: colors.textMuted,
                    marginTop: '0.25rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    flexWrap: 'wrap'
                  }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      <User size={12} />
                      {log.user_name || log.user_email || 'System'}
                    </span>
                    <span>•</span>
                    <span>
                      {new Date(log.created_at).toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                      })}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Changes Detail */}
              {formatChanges(log.changes, log.action)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
